import { useState, useEffect } from 'react';
import DynamicSettingsForm from '../DyanmicSettings/DynamicSettingsForm';
import { ITEM_SETTINGS_SCHEMA } from './ItemSchemas';
import CommandActions from './CommandActions';
import './ComponentSettings.css';

export default function ComponentSettings({ config, setConfig: setConfigOuter, selectedItem, setSelectedItem, materialBrowserProps }) {
  const items = config.items || {};
  const [showAddInput, setShowAddInput] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [duplicateSelected, setDuplicateSelected] = useState(false);

  const CLICK_TYPES = [
    'actions',
    'left-click',
    'right-click',
    'middle-click',
    'shift-left-click',
    'shift-right-click',
  ];

  useEffect(() => {
    const keys = Object.keys(items);
    if (!keys.includes(selectedItem)) {
      setSelectedItem(keys.length > 0 ? keys[0] : null);
    }
  }, [items]);

  const handleAddConfirm = () => {
    const trimmedName = newItemName.trim();
    if (!trimmedName) return;

    if (items[trimmedName]) {
      alert('Item with this name already exists.');
      return;
    }

    const newItemConfig = duplicateSelected && selectedItem
      ? structuredClone(items[selectedItem])
      : {};

    const newItems = { ...items, [trimmedName]: newItemConfig };
    setConfigOuter({
      ...config,
      items: newItems,
    });

    setSelectedItem(trimmedName);
    setNewItemName('');
    setDuplicateSelected(false);
    setShowAddInput(false);
  };

  const handleRemoveItem = () => {
    if (!selectedItem) return;

    const newItems = { ...items };
    delete newItems[selectedItem];

    const newlayout = {};
    if (config.layout) {
      for (const [slot, itemList] of Object.entries(config.layout)) {
        const filtered = itemList.filter((item) => item !== selectedItem);
        if (filtered.length > 0) newlayout[slot] = filtered;
      }
    }

    const hasItems = Object.keys(newItems).length > 0;
    const newConfig = { ...config };

    if (hasItems) {
      newConfig.items = newItems;
    } else {
      delete newConfig.items;
    }

    if (Object.keys(newlayout).length > 0) {
      newConfig.layout = newlayout;
    } else {
      delete newConfig.layout;
    }

    setConfigOuter(newConfig);
    setSelectedItem(hasItems ? Object.keys(newItems)[0] : null);
  };

  const handleSelectChange = (e) => {
    setSelectedItem(e.target.value);
  };

  // Regex to allow letters, numbers, %, { }, [ ], _, -
  const allowedCharsRegex = /^[a-zA-Z0-9%{}\[\]_\-]*$/;

  return (
    <div className="component-settings panel-settings-container">

      <h3 style={{ marginBottom: '1.5rem', marginTop: '0rem' }}>Item Configuration</h3>
      <div className="component-settings-header" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
        {!showAddInput && (
          <button onClick={() => setShowAddInput(true)} className="add-button" type="button">+</button>
        )}

        {showAddInput && (
          <>
            <input
              type="text"
              placeholder="New item name"
              value={newItemName}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '' || allowedCharsRegex.test(val)) {
                  setNewItemName(val);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddConfirm();
                if (e.key === 'Escape') {
                  setShowAddInput(false);
                  setDuplicateSelected(false);
                }
              }}
              style={{
                background: '#1e2126',
                border: '1px solid #4b5563',
                color: '#fff',
                padding: '6px 8px',
                borderRadius: '4px',
                fontFamily: 'inherit',
                fontSize: '14px',
                width: '180px',
              }}
            />
            <button onClick={handleAddConfirm} disabled={!newItemName.trim()} className="add-button add-confirm" type="button">+</button>
            <button onClick={() => { setShowAddInput(false); setDuplicateSelected(false); }} className="cancel-button" type="button">×</button>
            {selectedItem && (
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={duplicateSelected}
                  onChange={(e) => setDuplicateSelected(e.target.checked)}
                />
                Clone Selected Item
              </label>
            )}
          </>
        )}

        {Object.keys(items).length > 0 && !showAddInput ? (
          <>
            <select value={selectedItem || ''} onChange={handleSelectChange} aria-label="Select item">
              {Object.keys(items).map((itemKey) => (
                <option key={itemKey} value={itemKey}>{itemKey}</option>
              ))}
            </select>

            <button onClick={handleRemoveItem} disabled={!selectedItem} className="remove-button" type="button">−</button>
          </>
        ) : (
          !showAddInput && (
            <span style={{ marginLeft: '1rem', fontStyle: 'italic', color: 'lightgray' }}>
              No items available. Add one to get started.
            </span>
          )
        )}
      </div>

      {selectedItem && (
        <>
          <div className="layout-overview">
            <DynamicSettingsForm
              panelType={config.type}
              config={items[selectedItem]}
              setConfig={(updatedItemConfig) => {
                setConfigOuter({
                  ...config,
                  items: {
                    ...items,
                    [selectedItem]: updatedItemConfig,
                  },
                });
              }}
              settingsSchema={ITEM_SETTINGS_SCHEMA[config.type]}
              title="Component Settings"
              materialBrowserProps={materialBrowserProps}
            />
          </div>
          <h3>Command Actions</h3>
          <CommandActions
            panelType={config.type}
            itemType={items[selectedItem]?.type}
            actions={
              // Pass action keys directly from item, filter only valid click types
              CLICK_TYPES.reduce((acc, clickType) => {
                if (items[selectedItem]?.[clickType]) {
                  acc[clickType] = items[selectedItem][clickType];
                }
                return acc;
              }, {})
            }
            setActions={(updatedActions) => {
              // Filter out non-action keys from the current item
              const nonActionKeys = Object.keys(items[selectedItem] || {}).filter(
                (key) => !CLICK_TYPES.includes(key)
              );

              // Create updated item merging non-action keys and new actions
              const updatedItem = {
                ...nonActionKeys.reduce((obj, key) => {
                  obj[key] = items[selectedItem][key];
                  return obj;
                }, {}),
                ...updatedActions,
              };

              setConfigOuter({
                ...config,
                items: {
                  ...items,
                  [selectedItem]: updatedItem,
                },
              });
            }}
          />
        </>
      )}
    </div>
  );
}
